import * as React from "react";
import * as moment from "moment";
import * as _ from "lodash";
import {lazyInject} from "@athena-portal/core";
import {CoverChange} from "@ferry/journey-services";
import {Types} from "../../../../../Types";
import type {ICustomerPortalService} from "../../../../../services/ICustomerPortalService";
import {InfoBanner, WarningBanner} from "@ferry/portal-core";

interface IState {
    isLoading: boolean;
    coverChanges: CoverChange[];
}

interface IProps {
    policyNumber: string,
    policyEndDate: string,
    upcomingMtaDate?: string;
}

export class FutureMTAExplainedHeader extends React.Component<IProps, IState> {

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    constructor(props: any) {
        super(props);

        this.state = {
            isLoading: true,
            coverChanges: []
        };
    }

    public async componentDidMount() {
        this.setState({isLoading: true});
        this.customerPortalService.getCoverChanges(this.props.policyNumber).subscribe(
            (coverChanges: CoverChange[]) => {
                this.setState({
                    coverChanges,
                    isLoading: false
                })
            }
        )
    }

    public render() {
        if (!this.state.isLoading && this.props.upcomingMtaDate && this.state.coverChanges) {
            const mtaOnEndDateOfPolicy: boolean = moment.utc(this.props.policyEndDate).isSame(moment.utc(this.props.upcomingMtaDate), 'day');
            const additionalDescription: any = mtaOnEndDateOfPolicy ?
                (<>
                    You can't make any additional changes to this policy as the existing change date is your policy
                    expiry date.
                    <br/>
                    If you have renewed your policy you can make a change to your new policy from its policy start date
                </>) :
                "The earliest opportunity to make additional changes is " + this.getNextPossibleMtaDate(this.props.upcomingMtaDate!, "DD/MM/YYYY")
            if (this.state.coverChanges) {
                return (
                    <InfoBanner wording={
                        <>
                            <p className="grid-col-2">
                                <b>Heads up</b> – {this.hasChangesStartingToday()
                                ? "you have already made changes to take effect from today."
                                : "you have made changes to take effect from future dates:"}
                            </p>
                            {this.generateCoverChangeText(this.state.coverChanges)}
                            <div className="grid-col-2 margin-top-16"><strong>{additionalDescription}</strong></div>
                        </>
                    }/>
                );
            } else {
                return (
                    <WarningBanner wording={
                        <>
                            <strong>You have a future change that takes effect on {this.convertDate(this.props.upcomingMtaDate, "DD/MM/YYYY")}</strong>
                            <p>{additionalDescription}</p>
                        </>
                    }/>
                );
            }
        } else {
            return <></>;
        }
    }

    public generateCoverChangeText(coverChanges: CoverChange[]) {
        const groupedByStartDate = _.groupBy(coverChanges, "startDate");

        let textLines: string[] = [];

        Object.entries(groupedByStartDate).forEach(([startDate, changes]) => {
            const textLinesCoverTier = this.transformCoverTierIntoTextLines(changes);
            const textLinesOptionalExtras = this.transformOptionalExtrasIntoTextLines(changes);
            const textLinesMTA = this.transformMTAChangeIntoTextLines(changes);

            const coverChangesText = textLinesCoverTier.concat(textLinesOptionalExtras, textLinesMTA).join(", ");
            const line = this.convertDate(startDate, "DD/MM/YYYY") + ": " + _.capitalize(coverChangesText);

            textLines.push(line);
        })

        const output = textLines.map((line: string, index) =>
            <li className="margin-bottom-8 cp-letter" key={index}>{line}</li>
        );
        return (<ul className="grid-col-2 m-0">{output}</ul>);
    }

    public transformCoverTierIntoTextLines(coverChanges: CoverChange[]) {
        return coverChanges
            .filter(coverChange => coverChange.type === "COVER_TIER")
            .map(coverChange =>
                `Upgraded cover tier from ${this.convertCoverTier(coverChange.previousValue)} to ${this.convertCoverTier(coverChange.currentValue)}`);
    }

    private transformMTAChangeIntoTextLines(coverChanges: CoverChange[]) {
        return coverChanges
            .filter(coverChange => this.isMTAChange(coverChange))
            .map(coverChange => this.getMtaChangeText(coverChange));
    }

    private getMtaChangeText(coverChange: CoverChange) {
        switch (coverChange.type) {
            case "REPLACE_CAR":
                return `Replaced ${coverChange.previousValue}`;
            case "ADD_DRIVER":
                return `Added ${coverChange.currentValue}`;
            case "EDIT_DRIVER":
                return `Edited ${coverChange.currentValue}`;
            case "REMOVE_DRIVER":
                return `Removed ${coverChange.currentValue}`;
            case "CHANGE_ADDRESS":
                return `Changed Address`;
            case "EDIT_CAR_DETAILS":
                return `Edited Car Details`;
            case "EDIT_CUSTOMER_DETAILS":
                return `Edited ${coverChange.currentValue}`;
            case "EDIT_CAR_REGISTRATION_NUMBER":
                return `Updated registration number from ${coverChange.previousValue} to ${coverChange.currentValue}`;
            default:
                return "";
        }
    }

    private transformOptionalExtrasIntoTextLines(coverChanges: CoverChange[]) {
        let optionalExtrasChanges = coverChanges.filter(coverChange => coverChange.type === "OPTIONAL_EXTRA");

        return this.constructTextLines(optionalExtrasChanges, []);
    }

    private isMTAChange(coverChange: CoverChange) {
        const mtaChangeTypes = [
            "REPLACE_CAR",
            "ADD_DRIVER",
            "EDIT_DRIVER",
            "REMOVE_DRIVER",
            "CHANGE_ADDRESS",
            "EDIT_CAR_DETAILS",
            "EDIT_CUSTOMER_DETAILS",
            "EDIT_CAR_REGISTRATION_NUMBER"
        ];

        return mtaChangeTypes.includes(coverChange.type);
    }

    private constructTextLines(optionalExtras: CoverChange[], lines: string[]) {
        optionalExtras.forEach(coverChange => lines.push(
            this.convertOptionalExtraAction(coverChange) + " " + this.convertCoverType(coverChange.changedField)));

        return lines;
    }

    private convertOptionalExtraAction(coverChange: CoverChange) {
        switch (coverChange.action) {
            case "ADDED":
                return "Added";
            case "REMOVED":
                return "Removed";
            default:
                return "";
        }
    }

    private convertCoverType = (coverType: string) => {
        switch (coverType) {
            case "FAMILY_PACKAGE":
                return "Family package";
            case "PROTECTED_NCD":
                return "Protected no claim discount";
            case "COURTESY_CAR":
                return "Guaranteed Hire Car";
            case "LEGAL_EXPENSES":
                return "Motor legal expenses";
            default:
                throw new Error(`Unknown cover type.`);
        }
    }

    private convertCoverTier = (coverTier: string) => {
        switch (coverTier) {
            case "ALLIANZ":
                return "Plus";
            case "ALLIANZ_ESSENTIALS":
                return "Essentials"
            default:
                return "Plus";
        }
    }

    private convertDate = (inputDate: string, format: string) => {
        const valueAsMoment = moment.utc(inputDate, moment.ISO_8601);
        if (valueAsMoment.isValid()) {
            return (valueAsMoment.format(format))
        }
        return null;
    };

    private getNextPossibleMtaDate = (inputDate: string, format: string) => {
        const valueAsMoment = moment.utc(inputDate, moment.ISO_8601);
        if (valueAsMoment.isValid()) {
            return (valueAsMoment.add(1, "days").format(format))
        }
        return null;
    };

    private hasChangesStartingToday(): boolean {
        const todayFormatted = this.convertDate(new Date().toISOString(), "DD/MM/YYYY");
        if (this.props.upcomingMtaDate)
        {
            const formattedUpcomingMtaDate = this.convertDate(this.props.upcomingMtaDate, "DD/MM/YYYY")
            return formattedUpcomingMtaDate === todayFormatted;
        }
        return this.state.coverChanges.some((change: CoverChange) => {
            const isoDate = new Date(change.startDate).toISOString();
            const formattedStartDate = this.convertDate(isoDate, "DD/MM/YYYY");
            return formattedStartDate === todayFormatted;
        });
    }
}
